import { useEffect, useState, useCallback, useRef } from 'react';
import { todayLocal } from '../utils/dateLocal';
import axios from 'axios';
import { WinSectionHeader } from './WinComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Labels + couleurs pour l'affichage du badge d'onglet à côté de chaque
// item de la liste "Info manquante" — parité UX legacy où le message
// dans lstInfoManq mentionnait implicitement le tab par sa formulation
// (ex: "Nom du requérant" → onglet Requérant).
const TAB_BADGE = {
  requerant: { label: 'Requérant', color: '#1e5aa8', bg: '#e0edff' },
  conjoint:  { label: 'Conjoint',  color: '#a35a00', bg: '#fff3e0' },
  actifs:    { label: 'Actifs',    color: '#7b1fa2', bg: '#f3e5f5' },
  revenus:   { label: 'Revenus',   color: '#2e7d32', bg: '#e8f5e9' },
  liens:     { label: 'Liens',     color: '#00838f', bg: '#e0f7fa' },
  service:   { label: 'Service',   color: '#c62828', bg: '#ffebee' },
  decision:  { label: 'Décision',  color: '#4527a0', bg: '#ede7f6' },
  statut:    { label: 'Statut',    color: '#546e7a', bg: '#eceff1' },
};
const _tabBadge = (tab) => TAB_BADGE[tab] || TAB_BADGE.statut;

/**
 * Onglet Statut — Reproduit fidèlement l'écran VB.NET du legacy.
 *
 *   1. Statut actuel (read-only) : addastatut, addadatestatut, addadatedepuis,
 *      addaferme
 *   2. Dates clés (read-only) : addadatesigdec, addadatereccarte,
 *      addachemine, addadatfrm
 *   3. lstInfoManq (ListBox rouge, auto-calculée) — reproduction fidèle
 *      du contrôle legacy `MDIParent2.lstInfoManq` avec :
 *        - Liste des items manquants calculée par le backend (ChkStatDaj)
 *        - Double-clic → navigation vers l'onglet + focus champ
 *        - Bouton `btnSignOK` conditionnel pour estampiller signatures/carte
 *   4. Actions : Cheminer, Fermer dossier
 *   5. DataGridStatut : historique des révisions du client
 *
 * Legacy refs :
 *   - Codes/Validations.vb :: ChkStatDaj() (règles de validation)
 *   - MDIParent2.vb :: lstInfoManq_DoubleClick (navigation)
 *   - MDIParent2.vb :: btnSignOK_Click / chkBtnSignOK (bouton signer)
 */
const StatutTab = ({ formData, setFormData, refreshDossier, setCurrentTab, onMissingItemsChange, onStatutChange }) => {
  const noclient = formData?.noclient || formData?.no_client || '';
  const noseqdaj = formData?.no_sequence || '';

  // ============= Historique (DataGrid) =============
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedNoseq, setSelectedNoseq] = useState(noseqdaj);
  const [sortKey, setSortKey] = useState('no_sequence_complet');
  const [sortDir, setSortDir] = useState('asc');

  // ============= lstInfoManq (reproduction legacy) =============
  const [infoManqItems, setInfoManqItems] = useState([]);
  const [infoManqStatut, setInfoManqStatut] = useState('');
  const [infoManqCode, setInfoManqCode] = useState(0);
  const [addastatutFresh, setAddastatutFresh] = useState('');
  const [selectedItemIdx, setSelectedItemIdx] = useState(-1);
  const [infoManqLoading, setInfoManqLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [signInProgress, setSignInProgress] = useState(false);
  const infoManqReloadRef = useRef(null);

  // === cboSignatureType (Static.dbo.tSignatureType) =============
  // Legacy: MDIParent2.vb:1466 → requis avant btnSignOK. Persisté dans
  // daj.champVide1 via /info-manquante-sign. Aussi éditable via
  // /daj/{id}/type-signature pour les dossiers déjà complets.
  const [signatureTypes, setSignatureTypes] = useState([]);
  const [selectedSignatureType, setSelectedSignatureType] = useState('');
  const [persistedSignatureType, setPersistedSignatureType] = useState('');
  const [savingSignatureType, setSavingSignatureType] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reference/signature-types`);
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.log('[StatutTab] /reference/signature-types →', data?.length, 'items', data);
        setSignatureTypes((data || []).map(r => ({
          value: (r.DsSignaType || '').trim(),
          label: (r.DsSignaType || '').trim(),
          id: r.SignaTypeID,
        })));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[StatutTab] /reference/signature-types ERROR:', e?.response?.status, e?.response?.data || e.message);
        if (!cancelled) setSignatureTypes([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Précharger la valeur legacy déjà persistée (champVide1)
  useEffect(() => {
    const persisted = (formData?.champVide1 || formData?.type_signature || '').trim();
    if (persisted) {
      setSelectedSignatureType(persisted);
      setPersistedSignatureType(persisted);
    }
  }, [formData?.champVide1, formData?.type_signature]);

  // Sauvegarder le type sans stamper de date (dossiers complets)
  const doSaveTypeSignature = async () => {
    if (!noseqdaj || savingSignatureType) return;
    setSavingSignatureType(true);
    try {
      await axios.patch(
        `${API}/daj/${encodeURIComponent(noseqdaj)}/type-signature`,
        { signature_type: selectedSignatureType }
      );
      setPersistedSignatureType(selectedSignatureType);
      setActionMsg(`Type de signature enregistré : "${selectedSignatureType || '(vide)'}".`);
      if (refreshDossier) refreshDossier();
    } catch (e) {
      setActionMsg(`Erreur : ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingSignatureType(false);
      setTimeout(() => setActionMsg(''), 3000);
    }
  };

  // === Reload historique révisions ===
  const reload = useCallback(async () => {
    if (!noclient) { setItems([]); return; }
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(
        `${API}/clients/${encodeURIComponent(noclient)}/statut-history`
      );
      setItems(data?.items || []);
      if (noseqdaj) setSelectedNoseq(noseqdaj);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Erreur');
      setItems([]);
    } finally { setLoading(false); }
  }, [noclient, noseqdaj]);

  useEffect(() => { reload(); }, [reload]);

  // === Reload lstInfoManq (auto-check via backend) ===
  const reloadInfoManq = useCallback(async () => {
    if (!noseqdaj) { setInfoManqItems([]); setInfoManqStatut(''); return; }
    setInfoManqLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/daj/${encodeURIComponent(noseqdaj)}/info-manquante-check`
      );
      setInfoManqItems(data?.items || []);
      setInfoManqStatut(data?.statut || '');
      setInfoManqCode(data?.statut_code_r ?? 0);
      setAddastatutFresh((data?.addastatut || '').trim());
      // Propage au parent App.js pour synchroniser l'en-tête (parité
      // affichage StatutTab.displayStatut = addastatut / chemine).
      if (onMissingItemsChange) onMissingItemsChange(data?.items || []);
      if (onStatutChange) onStatutChange({
        label: data?.statut || '',
        code: data?.statut_code_r ?? 0,
        addastatut: (data?.addastatut || '').trim(),
      });
      // Précharger le type de signature persistée depuis la réponse
      const ts = (data?.type_signature || '').trim();
      if (ts) {
        setSelectedSignatureType(ts);
        setPersistedSignatureType(ts);
      }
      if ((data?.items || []).length <= selectedItemIdx) setSelectedItemIdx(-1);
    } catch (e) {
      setInfoManqItems([]);
      setInfoManqStatut('Erreur');
    } finally { setInfoManqLoading(false); }
  }, [noseqdaj, selectedItemIdx]);

  useEffect(() => { reloadInfoManq(); }, [reloadInfoManq]);

  // Refresh lstInfoManq quand des champs pertinents changent (debounce 400ms)
  useEffect(() => {
    if (infoManqReloadRef.current) clearTimeout(infoManqReloadRef.current);
    infoManqReloadRef.current = setTimeout(() => { reloadInfoManq(); }, 400);
    return () => { if (infoManqReloadRef.current) clearTimeout(infoManqReloadRef.current); };
  }, [reloadInfoManq, formData?.info_manquante, formData?.date_signature_declarant,
      formData?.date_reception_carte, formData?.date_cheminement,
      formData?.dossier_ferme, formData?.statut]);

  const sortedItems = [...items].sort((a, b) => {
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    if (av === bv) return 0;
    const cmp = String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  // === Actions ===
  const doCheminer = async () => {
    if (!noseqdaj) return;
    if (!window.confirm('Confirmer le cheminement du dossier ? (Legacy : addachemine = maintenant)')) return;
    try {
      await axios.post(`${API}/daj/${noseqdaj}/cheminer`);
      setActionMsg('Dossier cheminé (date acheminement mise à jour).');
      if (refreshDossier) refreshDossier();
      reload(); reloadInfoManq();
    } catch (e) { setActionMsg(`Erreur : ${e?.response?.data?.detail || e.message}`); }
    setTimeout(() => setActionMsg(''), 3000);
  };

  // === Fermeture (dossier/demande selon l'avocat confié — parité frmFermDaj) ===
  const [fermInfo, setFermInfo] = useState(null);   // réponse GET fermeture-info
  const [fermCode, setFermCode] = useState('');
  const [fermDate, setFermDate] = useState('');
  const [fermSaving, setFermSaving] = useState(false);
  const [fermError, setFermError] = useState('');
  const lettreAvo = (formData?.decision?.dossier_confie_code || formData?.dossier_confie_a || '')
    .trim().charAt(0).toUpperCase();
  const fermLibelle = lettreAvo === 'A' ? 'Fermer le dossier' : 'Fermer la demande';

  const ouvrirFermeture = async () => {
    if (!noseqdaj) return;
    setFermError('');
    try {
      const { data } = await axios.get(`${API}/daj/${encodeURIComponent(noseqdaj)}/fermeture-info`);
      setFermInfo(data);
      setFermCode(data.deja_ferme && data.code_actuel ? String(data.code_actuel) : '');
      setFermDate(data.deja_ferme && data.date_fermeture_actuelle
        ? data.date_fermeture_actuelle
        : todayLocal());
    } catch (e) {
      setActionMsg(`Erreur : ${e?.response?.data?.detail || e.message}`);
      setTimeout(() => setActionMsg(''), 4000);
    }
  };

  const doFermer = async (confirmerDejaFerme = false) => {
    if (!fermCode) { setFermError('Choisissez un code de fermeture dans la liste.'); return; }
    setFermSaving(true);
    setFermError('');
    try {
      const { data } = await axios.post(`${API}/daj/${encodeURIComponent(noseqdaj)}/fermer`, {
        code: parseInt(fermCode, 10),
        date_fermeture: fermDate,
        confirmer_deja_ferme: confirmerDejaFerme,
      });
      setFermInfo(null);
      setActionMsg(`${data.type === 'dossiers' ? 'Dossier fermé' : 'Demande fermée'} (${data.abrege}) — statut : ${data.statut}`);
      // Reflète immédiatement la fermeture dans l'onglet Statut
      if (setFormData) {
        setFormData(prev => ({
          ...prev,
          dossier_ferme: true,
          date_fermeture_statut: fermDate,
          statut: data.statut,
        }));
      }
      if (refreshDossier) refreshDossier();
      reload(); reloadInfoManq();
      setTimeout(() => setActionMsg(''), 5000);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (e?.response?.status === 409) {
        if (window.confirm(detail)) { setFermSaving(false); doFermer(true); return; }
      } else {
        setFermError(detail);
      }
    } finally {
      setFermSaving(false);
    }
  };

  const doReactiver = async () => {
    if (!noseqdaj) return;
    if (!window.confirm(`Réactiver ${lettreAvo === 'A' ? 'ce dossier' : 'cette demande'} ?\nLa fermeture sera annulée.`)) return;
    try {
      const { data } = await axios.post(`${API}/daj/${encodeURIComponent(noseqdaj)}/reactiver`);
      setActionMsg(`${data.type === 'dossiers' ? 'Dossier réactivé' : 'Demande réactivée'} — statut : ${data.statut}`);
      if (setFormData) {
        setFormData(prev => ({ ...prev, dossier_ferme: false, date_fermeture_statut: null, statut: data.statut }));
      }
      if (refreshDossier) refreshDossier();
      reload(); reloadInfoManq();
      setTimeout(() => setActionMsg(''), 5000);
    } catch (e) {
      setActionMsg(`Erreur : ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Legacy : MDIParent2.vb:5224-5332 (double-clic sur lstInfoManq amène au champ)
  const handleItemDoubleClick = (item) => {
    if (!item) return;
    if (!item.tab || !setCurrentTab) return;
    setCurrentTab(item.tab);
    if (!item.field) return;
    // Focus différé pour laisser le temps à l'onglet cible de se monter.
    // Essaie plusieurs variantes de sélecteur (nos data-testid mixent tirets
    // et underscores selon les composants).
    const fieldName = item.field;
    const fieldDash = fieldName.replace(/_/g, '-');
    const selectors = [
      `[data-testid="input-${fieldName}"]`,
      `[data-testid="input-${fieldDash}"]`,
      `[data-testid="${fieldName}"]`,
      `[data-testid="${fieldDash}"]`,
      `[name="${fieldName}"]`,
      `input[id="${fieldName}"]`,
    ];
    setTimeout(() => {
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el) {
          // Highlight visuel + focus (parité MDIParent2 qui utilise .Focus + BackColor)
          if (el.focus) el.focus();
          if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          try {
            el.style.outline = '2px solid #ff9c00';
            el.style.transition = 'outline 0.3s';
            setTimeout(() => { el.style.outline = ''; }, 2000);
          } catch { /* noop */ }
          return;
        }
      }
      // Aucun sélecteur n'a matché — log discret pour aider le debug
      // (l'utilisateur voit quand même l'onglet cible)
      console.warn(`[StatutTab] Champ introuvable pour navigation: ${fieldName}`);
    }, 150);
  };

  // === lstInfoManq — Bouton "Signer OK" (estampille date) ===
  // Legacy : MDIParent2.vb:1463-1477 (btnSignOK_Click) + chkBtnSignOK
  // ⚠ Demande utilisateur (2026-02-16 v8) : pour les items SIGNATURE, ne PAS
  // proposer le raccourci "Signature OK" — l'utilisateur préfère naviguer
  // (double-clic) puis saisir la date manuellement dans l'onglet cible. Le
  // raccourci reste actif uniquement pour "Carte de sécurité de rev." où
  // l'estampille de la date de réception fait sens.
  const currentItem = selectedItemIdx >= 0 ? infoManqItems[selectedItemIdx] : null;
  const signSuffix = (() => {
    if (!currentItem) return '';
    const lbl = currentItem.label || '';
    if (lbl.includes('Carte de sécurité de rev'))  return 'Carte OK';
    return '';
  })();

  const doSignItem = async () => {
    if (!currentItem || !signSuffix || !noseqdaj) return;
    // Legacy MDIParent2.vb:1466 : Type de signature requis pour Signature*
    const isSignature = signSuffix === 'Signature OK';
    if (isSignature && !selectedSignatureType) {
      setActionMsg('Erreur : Vous devez indiquer le type de signature.');
      setTimeout(() => setActionMsg(''), 3500);
      return;
    }
    setSignInProgress(true);
    try {
      const { data } = await axios.post(
        `${API}/daj/${encodeURIComponent(noseqdaj)}/info-manquante-sign`,
        { item_label: currentItem.label, signature_type: selectedSignatureType }
      );
      setInfoManqItems(data?.items || []);
      if (onMissingItemsChange) onMissingItemsChange(data?.items || []);
      setInfoManqStatut(data?.statut || '');
      setInfoManqCode(data?.statut_code_r ?? 0);
      setAddastatutFresh((data?.addastatut || '').trim());
      // Remonte le statut complet au parent pour l'en-tête (parité affichage StatutTab)
      if (onStatutChange) onStatutChange({
        label: data?.statut || '',
        code: data?.statut_code_r ?? 0,
        addastatut: (data?.addastatut || '').trim(),
      });
      setSelectedItemIdx(-1);
      setActionMsg(`${signSuffix} : date estampillée pour "${currentItem.label}".`);
      // ⚠ Ne PAS auto-remplir le champ `Date signature req.` côté UI :
      // l'utilisateur préfère saisir la date lui-même via l'onglet cible
      // (double-clic sur la ligne d'info manquante y amène). Cf. demande
      // utilisateur 2026-02-16 (v7).
      if (refreshDossier) refreshDossier();
    } catch (e) {
      setActionMsg(`Erreur : ${e?.response?.data?.detail || e.message}`);
    } finally { setSignInProgress(false); setTimeout(() => setActionMsg(''), 3500); }
  };

  return (
    <div className="win-tab-content" data-testid="tab-statut-content">
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, padding: 8, alignItems: 'start' }}>

        {/* GAUCHE : liste des dossiers du client (colonnes triables) */}
        <fieldset className="win-groupbox" style={{ margin: 0 }}>
          <legend>Liste des dossiers du client</legend>
          <div style={{ overflow: 'auto', maxHeight: 420, border: '1px solid var(--primary-blue)' }} data-testid="statut-grid-wrapper">
            <table className="win-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ background: 'var(--primary-blue-pale)', color: 'var(--primary-blue)', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={thStyle} onClick={() => handleSort('no_sequence_complet')}>No Séquence {sortKey === 'no_sequence_complet' && (sortDir === 'asc' ? '▲' : '▼')}</th>
                  <th style={thStyle} onClick={() => handleSort('noref')}>No Référence {sortKey === 'noref' && (sortDir === 'asc' ? '▲' : '▼')}</th>
                  <th style={thStyle} onClick={() => handleSort('date_statut')}>Date {sortKey === 'date_statut' && (sortDir === 'asc' ? '▲' : '▼')}</th>
                  <th style={thStyle} onClick={() => handleSort('type_decision')}>Type {sortKey === 'type_decision' && (sortDir === 'asc' ? '▲' : '▼')}</th>
                  <th style={thStyle} onClick={() => handleSort('avocat_confie')}>Avocat confié {sortKey === 'avocat_confie' && (sortDir === 'asc' ? '▲' : '▼')}</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 12 }} data-testid="statut-loading">Chargement…</td></tr>}
                {!loading && error && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 12, color: 'red' }} data-testid="statut-error">{error}</td></tr>}
                {!loading && !error && sortedItems.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 12, color: '#666' }} data-testid="statut-empty">Aucun dossier.</td></tr>}
                {!loading && !error && sortedItems.map((it) => {
                  const isSelected = it.no_sequence_complet === selectedNoseq;
                  const typeColor = it.type_decision === 'Refus' ? '#c0392b'
                    : it.type_decision === 'Attestation' ? '#0a7d0a' : '#666';
                  return (
                    <tr key={it.no_sequence_complet || it.pk_daj}
                        onClick={() => setSelectedNoseq(it.no_sequence_complet)}
                        style={{ cursor: 'pointer', background: isSelected ? 'var(--primary-blue-hover)' : 'white' }}
                        data-testid={`statut-row-${it.no_sequence_complet}`}>
                      <td style={tdStyle}>{it.no_sequence_complet}</td>
                      <td style={tdStyle}>{it.noref}</td>
                      <td style={tdStyle}>{it.date_statut}</td>
                      <td style={{ ...tdStyle, color: typeColor, fontWeight: it.type_decision ? 600 : 400 }} data-testid={`statut-type-${it.no_sequence_complet}`}>{it.type_decision || '—'}</td>
                      <td style={tdStyle}>{it.avocat_confie || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </fieldset>

        {/* DROITE : info manquante + actions */}
        <fieldset className="win-groupbox" style={{ margin: 0 }}>
          <legend>Info manquante (double-cliquez sur un item pour y accéder)</legend>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'flex-start' }}>
          <div
            data-testid="lst-info-manq"
            style={{
              border: '2px inset #b0b0b0',
              background: '#ffffff',
              color: '#c00000',
              minHeight: 80,
              overflowY: 'visible',
              fontSize: 12,
              fontFamily: 'Tahoma, sans-serif',
              padding: 2,
            }}
          >
            {infoManqLoading && (
              <div style={{ padding: 6, color: '#666' }} data-testid="lst-info-manq-loading">Vérification…</div>
            )}
            {!infoManqLoading && infoManqItems.length === 0 && (
              <div style={{ padding: 6, color: '#0a7d0a', fontWeight: 600 }} data-testid="lst-info-manq-empty">
                ✓ Aucune information manquante — dossier complet.
              </div>
            )}
            {!infoManqLoading && infoManqItems.map((it, idx) => {
              const sel = idx === selectedItemIdx;
              const badge = _tabBadge(it.tab);
              return (
                <div
                  key={`${it.label}-${idx}`}
                  onClick={() => setSelectedItemIdx(idx)}
                  onDoubleClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleItemDoubleClick(it);
                  }}
                  style={{
                    padding: '2px 6px',
                    cursor: 'pointer',
                    background: sel ? '#316ac5' : 'transparent',
                    color: sel ? '#ffffff' : '#c00000',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  title={`Double-cliquez pour aller à l'onglet ${badge.label}${it.field ? ` (champ : ${it.field})` : ''}`}
                  data-testid={`lst-info-manq-item-${idx}`}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '1px 6px',
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 3,
                      color: sel ? '#ffffff' : badge.color,
                      background: sel ? 'rgba(255,255,255,0.25)' : badge.bg,
                      border: sel ? '1px solid rgba(255,255,255,0.5)' : `1px solid ${badge.color}`,
                      minWidth: 62,
                      textAlign: 'center',
                      flexShrink: 0,
                    }}
                    data-testid={`lst-info-manq-badge-${idx}`}
                  >
                    {badge.label}
                  </span>
                  <span style={{ flex: 1 }}>{it.label}</span>
                </div>
              );
            })}
          </div>

          {/* Colonne actions à droite */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
            {/* Type signature — déplacé ici, au-dessus de Rafraîchir */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ fontSize: 10, color: '#333', fontWeight: 600 }}>Type signature</label>
              {(() => {
                const known = new Set(signatureTypes.map(t => t.value));
                const currentVal = selectedSignatureType || persistedSignatureType || '';
                const legacyOpt = (currentVal && !known.has(currentVal))
                  ? { value: currentVal, label: `${currentVal} (legacy)` } : null;
                return (
                  <select className="win-select" value={currentVal}
                          onChange={(e) => setSelectedSignatureType(e.target.value)}
                          data-testid="select-signature-type-permanent"
                          style={{ fontSize: 11 }} disabled={formData?.dossier_ferme}>
                    <option value="">-- Choisir --</option>
                    {legacyOpt && (<option key="__legacy__" value={legacyOpt.value} style={{ fontStyle: 'italic', color: '#8b6914' }}>{legacyOpt.label}</option>)}
                    {signatureTypes.map(t => (<option key={t.id} value={t.value}>{t.label}</option>))}
                  </select>
                );
              })()}
              {selectedSignatureType !== persistedSignatureType && (
                <button type="button" className="win-btn" onClick={doSaveTypeSignature}
                        disabled={savingSignatureType || formData?.dossier_ferme}
                        style={{ fontSize: 10, padding: '2px 6px' }}
                        data-testid="btn-save-type-signature"
                        title="Enregistrer le type de signature">{savingSignatureType ? '…' : 'Enregistrer'}</button>
              )}
            </div>
            {signSuffix === 'Signature OK' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontSize: 10, color: '#333', fontWeight: 600 }}>
                  Type signature <span style={{ color: '#c00' }}>*</span>
                </label>
                <select
                  className="win-select"
                  value={selectedSignatureType}
                  onChange={(e) => setSelectedSignatureType(e.target.value)}
                  data-testid="select-signature-type"
                  style={{ fontSize: 11 }}
                >
                  <option value="">-- Choisir --</option>
                  {signatureTypes.map(t => (
                    <option key={t.id} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            )}
            {signSuffix && (
              <button
                type="button"
                className="win-btn"
                onClick={doSignItem}
                disabled={signInProgress || (signSuffix === 'Signature OK' && !selectedSignatureType)}
                title={signSuffix === 'Signature OK' && !selectedSignatureType
                  ? 'Sélectionnez d\'abord le type de signature'
                  : "Estampille la date du jour pour l'item sélectionné (legacy btnSignOK)"}
                style={{ background: '#e0f0ff', fontWeight: 700 }}
                data-testid="btn-sign-ok"
              >{signInProgress ? '…' : signSuffix}</button>
            )}
            <button
              type="button"
              className="win-btn"
              onClick={reloadInfoManq}
              disabled={!noseqdaj || infoManqLoading}
              data-testid="btn-refresh-info-manq"
            >Rafraîchir</button>
            <button
              type="button"
              className="win-btn"
              onClick={doCheminer}
              disabled={!noseqdaj || formData?.dossier_ferme}
              title="Marque le dossier acheminé (addachemine = maintenant)"
              data-testid="btn-cheminer"
            >Cheminer</button>
            <button
              type="button"
              className="win-btn"
              onClick={ouvrirFermeture}
              disabled={!noseqdaj}
              title={lettreAvo === 'A'
                ? 'Fermeture du dossier (avocat permanent — codes THO)'
                : 'Fermeture de la demande (codes PCSJ)'}
              style={{ color: '#8b0000', fontWeight: 700 }}
              data-testid="btn-fermer-dossier"
            >{fermLibelle}</button>
            {formData?.dossier_ferme && (
              <button
                type="button"
                className="win-btn"
                onClick={doReactiver}
                disabled={!noseqdaj}
                title={lettreAvo === 'A'
                  ? 'Réactiver le dossier (annule la fermeture)'
                  : 'Réactiver la demande (annule la fermeture)'}
                style={{ color: '#0a6b0a', fontWeight: 700 }}
                data-testid="btn-reactiver-demande"
              >{lettreAvo === 'A' ? 'Réactiver le dossier' : 'Réactiver la demande'}</button>
            )}
          </div>
        </div>
        {actionMsg && (
          <div style={{ marginTop: 6, fontSize: 11, color: actionMsg.startsWith('Erreur') ? '#c02020' : '#0a7d0a' }} data-testid="statut-action-msg">
            {actionMsg}
          </div>
        )}
        <div style={{ marginTop: 4, fontSize: 10, color: '#666' }}>
          {infoManqItems.length} item(s) manquant(s). Légende : <span style={{ color: '#c00' }}>(r)</span> = requis, (o) = optionnel.
        </div>
      </fieldset>
      </div>

      {/* Fenêtre de fermeture — parité frmFermDaj (codes FermEta dos/dem) */}
      {fermInfo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div style={{ background: '#f0f0f0', border: '1px solid #808080',
                        boxShadow: '3px 3px 8px rgba(0,0,0,0.3)', width: 520, maxWidth: '92vw' }}
               data-testid="fermeture-modal">
            <div style={{ background: 'linear-gradient(180deg,#1e5aa8,#164a8f)', color: '#fff',
                          padding: '6px 10px', fontWeight: 700, fontSize: 13,
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{fermInfo.type === 'dossiers' ? 'Fermeture du dossier' : 'Fermeture de la demande'}</span>
              <button onClick={() => setFermInfo(null)} data-testid="fermeture-close"
                      style={{ background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 14, fontSize: 12 }}>
              <table style={{ lineHeight: 1.8, marginBottom: 8 }}>
                <tbody>
                  <tr><td style={{ color: '#555', paddingRight: 12 }}>No séquence</td><td><strong>{noseqdaj}</strong></td></tr>
                  <tr><td style={{ color: '#555', paddingRight: 12 }}>Requérant</td><td>{fermInfo.prenom} {fermInfo.nom}</td></tr>
                  {fermInfo.avocat_confie && (
                    <tr><td style={{ color: '#555', paddingRight: 12 }}>Confié à</td><td>{fermInfo.avocat_confie}</td></tr>
                  )}
                  <tr><td style={{ color: '#555', paddingRight: 12 }}>Statut actuel</td><td>{fermInfo.statut || '—'}</td></tr>
                  {fermInfo.deja_ferme && (
                    <tr><td style={{ color: '#a00', paddingRight: 12 }}>Déjà fermé</td>
                        <td style={{ color: '#a00' }}>le {fermInfo.date_fermeture_actuelle} (code {fermInfo.code_actuel})</td></tr>
                  )}
                </tbody>
              </table>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 2 }}>Code de fermeture</label>
                <select value={fermCode} onChange={e => setFermCode(e.target.value)}
                        className="win-input" style={{ width: '100%' }}
                        data-testid="fermeture-code">
                  <option value="">-- Choisir dans la liste --</option>
                  {fermInfo.codes.map(c => (
                    <option key={c.code} value={c.code}>{c.abrege} — {c.code} — {c.description}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 2 }}>Date de fermeture</label>
                <input type="date" value={fermDate}
                       min={fermInfo.date_demande || undefined}
                       max={todayLocal()}
                       onChange={e => setFermDate(e.target.value)}
                       className="win-input" style={{ width: 180 }}
                       data-testid="fermeture-date" />
              </div>
              {fermError && (
                <div style={{ color: '#c02020', marginBottom: 8 }} data-testid="fermeture-error">{fermError}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="win-btn" onClick={() => setFermInfo(null)}
                        data-testid="fermeture-annuler">Annuler</button>
                <button type="button" className="win-btn"
                        onClick={() => doFermer(false)}
                        disabled={fermSaving || !fermCode}
                        style={{ background: '#8b0000', color: '#fff', fontWeight: 700 }}
                        data-testid="fermeture-enregistrer">
                  {fermSaving ? '…' : 'Enregistrer la fermeture'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '6px 8px', textAlign: 'left', cursor: 'pointer', userSelect: 'none', fontWeight: 600, borderBottom: '2px solid var(--primary-blue)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '4px 8px', borderBottom: '1px solid #e3e7f0' };

export default StatutTab;
